/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * ConfirmationDialog — Premium Radix Dialog
 * ============================================================================
 *
 * Animated confirmation dialog with glassmorphism, variant-colored glow,
 * shine sweep on action buttons, focus accent bar on textarea,
 * staggered content entrance, and prefers-reduced-motion support.
 *
 * ============================================================================
 */

import React, { useState, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, X, AlertTriangle, Loader2, Sparkles, Shield, Ban } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type DialogVariant = 'approve' | 'reject' | 'confirm' | 'warning' | 'danger';

interface ConfirmationDialogProps {
  isOpen: boolean;
  variant: DialogVariant;
  title: string;
  message: string;
  details?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  showInput?: boolean;
  inputPlaceholder?: string;
  inputLabel?: string;
  inputRequired?: boolean;
  isLoading?: boolean;
  onConfirm: (inputValue?: string) => void;
  onCancel: () => void;
}

export function ConfirmationDialog({
  isOpen,
  variant,
  title,
  message,
  details,
  confirmText,
  cancelText,
  showInput = false,
  inputPlaceholder,
  inputLabel,
  inputRequired = false,
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps) {
  const { t } = useTranslation('common');
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const resolvedCancelText = cancelText || t('confirmDialog.cancel');
  const resolvedInputPlaceholder = inputPlaceholder || t('confirmDialog.inputPlaceholder');
  const resolvedInputLabel = inputLabel || t('confirmDialog.inputLabel');

  const handleConfirm = () => {
    if (showInput && inputRequired && !inputValue.trim()) {
      inputRef.current?.focus();
      return;
    }
    onConfirm(inputValue || undefined);
  };

  const getVariantConfig = () => {
    switch (variant) {
      case 'approve':
        return {
          icon: <Check className="cd-icon-svg" />,
          iconClass: 'cd-ico-approve',
          btnClass: 'cd-btn-approve',
          glowClass: 'cd-glow-approve',
          defaultConfirmText: t('confirmDialog.buttons.approve'),
        };
      case 'reject':
        return {
          icon: <Ban className="cd-icon-svg" />,
          iconClass: 'cd-ico-reject',
          btnClass: 'cd-btn-reject',
          glowClass: 'cd-glow-reject',
          defaultConfirmText: t('confirmDialog.buttons.reject'),
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="cd-icon-svg" />,
          iconClass: 'cd-ico-warning',
          btnClass: 'cd-btn-warning',
          glowClass: 'cd-glow-warning',
          defaultConfirmText: t('confirmDialog.buttons.continue'),
        };
      case 'danger':
        return {
          icon: <Shield className="cd-icon-svg" />,
          iconClass: 'cd-ico-danger',
          btnClass: 'cd-btn-danger',
          glowClass: 'cd-glow-danger',
          defaultConfirmText: t('confirmDialog.buttons.delete'),
        };
      default:
        return {
          icon: <Sparkles className="cd-icon-svg" />,
          iconClass: 'cd-ico-confirm',
          btnClass: 'cd-btn-confirm',
          glowClass: 'cd-glow-confirm',
          defaultConfirmText: t('confirmDialog.buttons.confirm'),
        };
    }
  };

  const config = getVariantConfig();

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          onCancel();
          setInputValue('');
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="cd-backdrop" />
        <Dialog.Content
          className="cd-container"
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            if (showInput && inputRef.current) {
              e.preventDefault();
              inputRef.current.focus();
            }
          }}
          onEscapeKeyDown={(e) => { if (isLoading) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (isLoading) e.preventDefault(); }}
        >
          {/* Scan lines */}
          <div className="cd-scanlines" />

          {/* Glow border */}
          <div className={`cd-glow ${config.glowClass}`} />

          {/* Content */}
          <div className="cd-body">
            {/* Icon */}
            <div className={`cd-icon ${config.iconClass}`}>
              <div className="cd-icon-ring" />
              <div className="cd-icon-pulse" />
              {config.icon}
            </div>

            <Dialog.Title className="cd-title">{title}</Dialog.Title>
            <p className="cd-message">{message}</p>

            {details && <div className="cd-details">{details}</div>}

            {/* Input */}
            {showInput && (
              <div className="cd-input-group">
                <label className="cd-input-label">{resolvedInputLabel}</label>
                <div className="cd-input-wrap">
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={resolvedInputPlaceholder}
                    className="cd-textarea"
                    rows={3}
                    disabled={isLoading}
                  />
                  <div className="cd-input-accent" />
                </div>
                {inputRequired && (
                  <span className="cd-input-hint">
                    {inputValue.trim()
                      ? t('confirmDialog.characters', { count: inputValue.length })
                      : t('confirmDialog.required')}
                  </span>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="cd-actions">
              <button
                className="cd-btn cd-btn-cancel"
                onClick={onCancel}
                disabled={isLoading}
              >
                <X className="cd-btn-icon" />
                {resolvedCancelText}
              </button>
              <button
                className={`cd-btn ${config.btnClass}`}
                onClick={handleConfirm}
                disabled={isLoading || (showInput && inputRequired && !inputValue.trim())}
              >
                {/* Shine sweep */}
                <span className="cd-btn-shine" />
                {isLoading ? (
                  <>
                    <Loader2 className="cd-btn-icon cd-spinning" />
                    {t('confirmDialog.processing')}
                  </>
                ) : (
                  <>
                    {variant === 'approve' && <Check className="cd-btn-icon" />}
                    {variant === 'reject' && <Ban className="cd-btn-icon" />}
                    {confirmText || config.defaultConfirmText}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Corner decorations */}
          <div className="cd-corner cd-tl" />
          <div className="cd-corner cd-tr" />
          <div className="cd-corner cd-bl" />
          <div className="cd-corner cd-br" />
        </Dialog.Content>
      </Dialog.Portal>

      <style>{cdStyles}</style>
    </Dialog.Root>
  );
}

const cdStyles = `
  /* ── Backdrop ── */
  .cd-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(8px);
    z-index: var(--z-modal, 300);
  }
  .cd-backdrop[data-state="open"] {
    animation: cd-fade-in 300ms ease-out;
  }
  .cd-backdrop[data-state="closed"] {
    animation: cd-fade-out 250ms ease-in forwards;
  }

  @keyframes cd-fade-in {
    from { opacity: 0; backdrop-filter: blur(0); }
    to   { opacity: 1; backdrop-filter: blur(8px); }
  }
  @keyframes cd-fade-out {
    from { opacity: 1; }
    to   { opacity: 0; }
  }

  /* ── Container ── */
  .cd-container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 440px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 3px;
    z-index: calc(var(--z-modal, 300) + 1);
    overflow: hidden;
  }
  .cd-container[data-state="open"] {
    animation: cd-slide-in 400ms cubic-bezier(0.16,1,0.3,1);
  }
  .cd-container[data-state="closed"] {
    animation: cd-slide-out 300ms ease-in forwards;
  }

  @keyframes cd-slide-in {
    from { opacity: 0; transform: translate(-50%, -48%) scale(0.94); }
    to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
  @keyframes cd-slide-out {
    from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    to   { opacity: 0; transform: translate(-50%, -52%) scale(0.94); }
  }

  /* ── Scan lines ── */
  .cd-scanlines {
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      0deg, transparent, transparent 2px,
      rgba(255,255,255,0.01) 2px, rgba(255,255,255,0.01) 4px
    );
    pointer-events: none;
    z-index: 1;
  }

  /* ── Glow ── */
  .cd-glow {
    position: absolute;
    inset: -2px;
    border-radius: 5px;
    z-index: -1;
    opacity: 0.5;
    animation: cd-glow-pulse 3s ease-in-out infinite;
  }
  .cd-glow-approve  { background: linear-gradient(135deg,#10B981,#059669,#10B981); box-shadow: 0 0 40px rgba(16,185,129,0.3); }
  .cd-glow-reject   { background: linear-gradient(135deg,#EF4444,#DC2626,#EF4444); box-shadow: 0 0 40px rgba(239,68,68,0.3); }
  .cd-glow-warning  { background: linear-gradient(135deg,#F59E0B,#D97706,#F59E0B); box-shadow: 0 0 40px rgba(245,158,11,0.3); }
  .cd-glow-danger   { background: linear-gradient(135deg,#DC2626,#B91C1C,#DC2626); box-shadow: 0 0 40px rgba(220,38,38,0.3); }
  .cd-glow-confirm  { background: linear-gradient(135deg,#3B82F6,#2563EB,#3B82F6); box-shadow: 0 0 40px rgba(59,130,246,0.3); }

  @keyframes cd-glow-pulse {
    0%, 100% { opacity: 0.35; }
    50%      { opacity: 0.65; }
  }

  /* ── Body ── */
  .cd-body {
    position: relative;
    padding: 32px;
    z-index: 2;
    text-align: center;
  }

  /* ── Icon ── */
  .cd-icon {
    position: relative;
    width: 72px;
    height: 72px;
    margin: 0 auto 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cd-icon-svg {
    width: 32px;
    height: 32px;
    position: relative;
    z-index: 3;
    animation: cd-icon-pop 500ms cubic-bezier(0.34,1.56,0.64,1);
  }

  @keyframes cd-icon-pop {
    0%  { transform: scale(0); }
    60% { transform: scale(1.15); }
    100%{ transform: scale(1); }
  }

  .cd-icon-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid currentColor;
    opacity: 0.3;
    animation: cd-ring 2s ease-out infinite;
  }
  .cd-icon-pulse {
    position: absolute;
    inset: -8px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.1;
    animation: cd-pulse-out 2s ease-out infinite;
  }

  @keyframes cd-ring {
    0%, 100% { transform: scale(1); opacity: 0.3; }
    50%      { transform: scale(1.1); opacity: 0.5; }
  }
  @keyframes cd-pulse-out {
    0%  { transform: scale(0.8); opacity: 0.2; }
    50% { transform: scale(1.2); opacity: 0; }
    100%{ transform: scale(0.8); opacity: 0.2; }
  }

  .cd-ico-approve { background: linear-gradient(135deg,rgba(16,185,129,0.2),rgba(5,150,105,0.3)); color: #10B981; box-shadow: 0 0 30px rgba(16,185,129,0.3); }
  .cd-ico-reject  { background: linear-gradient(135deg,rgba(239,68,68,0.2),rgba(220,38,38,0.3));  color: #EF4444; box-shadow: 0 0 30px rgba(239,68,68,0.3); }
  .cd-ico-warning { background: linear-gradient(135deg,rgba(245,158,11,0.2),rgba(217,119,6,0.3)); color: #F59E0B; box-shadow: 0 0 30px rgba(245,158,11,0.3); }
  .cd-ico-danger  { background: linear-gradient(135deg,rgba(220,38,38,0.2),rgba(185,28,28,0.3));  color: #DC2626; box-shadow: 0 0 30px rgba(220,38,38,0.3); }
  .cd-ico-confirm { background: linear-gradient(135deg,rgba(59,130,246,0.2),rgba(37,99,235,0.3)); color: #3B82F6; box-shadow: 0 0 30px rgba(59,130,246,0.3); }

  /* ── Title / Message ── */
  .cd-title {
    font-family: var(--font-heading);
    font-size: 20px;
    font-weight: 700;
    color: var(--color-text-primary);
    margin: 0 0 12px;
    letter-spacing: -0.02em;
    animation: cd-text-in 400ms cubic-bezier(0.16,1,0.3,1) 80ms both;
  }
  .cd-message {
    font-size: 14px;
    color: var(--color-text-secondary);
    line-height: 1.6;
    margin: 0;
    animation: cd-text-in 400ms cubic-bezier(0.16,1,0.3,1) 140ms both;
  }

  @keyframes cd-text-in {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Details ── */
  .cd-details {
    margin-top: 16px;
    padding: 12px 16px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle, var(--color-border-default));
    border-radius: 3px;
    font-size: 13px;
    color: var(--color-text-primary);
    text-align: left;
    animation: cd-text-in 400ms cubic-bezier(0.16,1,0.3,1) 200ms both;
  }

  /* ── Input group ── */
  .cd-input-group {
    margin-top: 20px;
    text-align: left;
    animation: cd-text-in 400ms cubic-bezier(0.16,1,0.3,1) 200ms both;
  }
  .cd-input-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--color-text-secondary);
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .cd-input-wrap {
    position: relative;
    overflow: hidden;
    border-radius: 3px;
  }
  .cd-textarea {
    width: 100%;
    padding: 14px 16px;
    background: var(--color-bg-primary);
    border: 1.5px solid var(--color-border-default);
    border-radius: 3px;
    font-size: 14px;
    color: var(--color-text-primary);
    resize: none;
    font-family: inherit;
    transition: border-color 200ms cubic-bezier(0.16,1,0.3,1),
                box-shadow 200ms cubic-bezier(0.16,1,0.3,1);
    outline: none;
  }
  .cd-textarea:focus {
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 3px var(--color-brand-glow, rgba(139,0,0,0.1));
  }
  .cd-textarea::placeholder { color: var(--color-text-muted); }
  .cd-textarea:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Focus accent bar on textarea */
  .cd-input-accent {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: var(--color-brand-primary);
    transition: width 300ms cubic-bezier(0.16,1,0.3,1);
    pointer-events: none;
  }
  .cd-textarea:focus ~ .cd-input-accent {
    width: 100%;
  }

  .cd-input-hint {
    display: block;
    font-size: 11px;
    color: var(--color-text-muted);
    margin-top: 6px;
    text-align: right;
  }

  /* ── Actions ── */
  .cd-actions {
    display: flex;
    gap: 12px;
    margin-top: 28px;
    justify-content: center;
    animation: cd-text-in 400ms cubic-bezier(0.16,1,0.3,1) 260ms both;
  }

  .cd-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    font-family: var(--font-heading);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.04em;
    border-radius: 3px;
    border: none;
    cursor: pointer;
    min-width: 130px;
    overflow: hidden;
    transition: transform 250ms cubic-bezier(0.16,1,0.3,1),
                box-shadow 250ms cubic-bezier(0.16,1,0.3,1),
                background 200ms ease;
  }
  .cd-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
  .cd-btn .cd-btn-icon { width: 18px; height: 18px; }
  .cd-btn .cd-spinning { animation: cd-spin 0.9s linear infinite; }
  @keyframes cd-spin { to { transform: rotate(360deg); } }

  /* Shine sweep on action buttons */
  .cd-btn-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%);
    pointer-events: none;
  }
  .cd-btn:hover:not(:disabled) .cd-btn-shine {
    animation: cd-sweep 600ms cubic-bezier(0.16,1,0.3,1) forwards;
  }
  @keyframes cd-sweep {
    to { left: 120%; }
  }

  /* Cancel */
  .cd-btn-cancel {
    background: var(--color-bg-primary);
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border-default);
  }
  .cd-btn-cancel:hover:not(:disabled) {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
    transform: translateY(-1px);
  }
  .cd-btn-cancel:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
  }

  /* Variant buttons */
  .cd-btn-approve {
    background: linear-gradient(135deg,#10B981,#059669); color: white;
    box-shadow: 0 4px 15px rgba(16,185,129,0.3);
  }
  .cd-btn-approve:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 25px rgba(16,185,129,0.4); }
  .cd-btn-approve:active:not(:disabled) { transform: translateY(0) scale(0.97); }

  .cd-btn-reject {
    background: linear-gradient(135deg,#EF4444,#DC2626); color: white;
    box-shadow: 0 4px 15px rgba(239,68,68,0.3);
  }
  .cd-btn-reject:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 25px rgba(239,68,68,0.4); }
  .cd-btn-reject:active:not(:disabled) { transform: translateY(0) scale(0.97); }

  .cd-btn-warning {
    background: linear-gradient(135deg,#F59E0B,#D97706); color: white;
    box-shadow: 0 4px 15px rgba(245,158,11,0.3);
  }
  .cd-btn-warning:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 25px rgba(245,158,11,0.4); }
  .cd-btn-warning:active:not(:disabled) { transform: translateY(0) scale(0.97); }

  .cd-btn-danger {
    background: linear-gradient(135deg,#DC2626,#B91C1C); color: white;
    box-shadow: 0 4px 15px rgba(220,38,38,0.3);
  }
  .cd-btn-danger:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 25px rgba(220,38,38,0.4); }
  .cd-btn-danger:active:not(:disabled) { transform: translateY(0) scale(0.97); }

  .cd-btn-confirm {
    background: linear-gradient(135deg,#3B82F6,#2563EB); color: white;
    box-shadow: 0 4px 15px rgba(59,130,246,0.3);
  }
  .cd-btn-confirm:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 25px rgba(59,130,246,0.4); }
  .cd-btn-confirm:active:not(:disabled) { transform: translateY(0) scale(0.97); }

  /* ── Corner decorations ── */
  .cd-corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border-color: var(--color-border-default);
    opacity: 0.5;
  }
  .cd-tl { top: 8px; left: 8px; border-left: 2px solid; border-top: 2px solid; border-top-left-radius: 4px; }
  .cd-tr { top: 8px; right: 8px; border-right: 2px solid; border-top: 2px solid; border-top-right-radius: 4px; }
  .cd-bl { bottom: 8px; left: 8px; border-left: 2px solid; border-bottom: 2px solid; border-bottom-left-radius: 4px; }
  .cd-br { bottom: 8px; right: 8px; border-right: 2px solid; border-bottom: 2px solid; border-bottom-right-radius: 4px; }

  /* ── Mobile ── */
  @media (max-width: 480px) {
    .cd-container { width: 95%; border-radius: 16px; }
    .cd-body { padding: 24px 20px; }
    .cd-icon { width: 64px; height: 64px; margin-bottom: 20px; }
    .cd-icon-svg { width: 28px; height: 28px; }
    .cd-title { font-size: 18px; }
    .cd-message { font-size: 13px; }
    .cd-actions { flex-direction: column-reverse; }
    .cd-btn { width: 100%; padding: 14px 24px; }
  }

  /* ── Light theme ── */
  [data-theme="light"] .cd-backdrop { background: rgba(0,0,0,0.5); }
  [data-theme="light"] .cd-scanlines {
    background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.01) 2px, rgba(0,0,0,0.01) 4px);
  }
  [data-theme="light"] .cd-glow { opacity: 0.35; }
  [data-theme="light"] .cd-corner { opacity: 0.25; }

  /* ── Reduced motion ── */
  @media (prefers-reduced-motion: reduce) {
    .cd-backdrop,
    .cd-container,
    .cd-icon-svg,
    .cd-icon-ring,
    .cd-icon-pulse,
    .cd-glow,
    .cd-title,
    .cd-message,
    .cd-details,
    .cd-input-group,
    .cd-actions,
    .cd-btn,
    .cd-btn-shine,
    .cd-input-accent,
    .cd-textarea {
      animation: none !important;
      transition-duration: 0ms !important;
    }
  }
`;

export default ConfirmationDialog;
